import { PDFDocument } from 'pdf-lib';
import Tesseract from 'tesseract.js';

/**
 * Automatic Form Detection - Detect and extract form fields from PDFs
 */

// Detect form fields in PDF
export const detectFormFields = async (file, onProgress) => {
  onProgress(0);
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    
    onProgress(20);
    
    const form = pdfDoc.getForm();
    const fields = form.getFields();
    
    onProgress(40);
    
    const detectedFields = [];
    
    fields.forEach((field, index) => {
      const fieldName = field.getName();
      const fieldType = field.constructor.name;
      
      let fieldInfo = {
        name: fieldName,
        type: fieldType.replace('PDF', '').toLowerCase(),
        required: field.isRequired(),
        readOnly: field.isReadOnly(),
      };
      
      try {
        if (fieldType === 'PDFTextField') {
          fieldInfo.value = field.getText();
          fieldInfo.maxLength = field.getMaxLength();
        } else if (fieldType === 'PDFCheckBox') {
          fieldInfo.checked = field.isChecked();
        } else if (fieldType === 'PDFDropdown') {
          fieldInfo.options = field.getOptions();
          fieldInfo.selected = field.getSelected();
        } else if (fieldType === 'PDFRadioGroup') {
          fieldInfo.options = field.getOptions();
          fieldInfo.selected = field.getSelected();
        }
      } catch (error) {
        fieldInfo.error = error.message;
      }
      
      detectedFields.push(fieldInfo);
      onProgress(40 + (index / fields.length) * 50);
    });
    
    onProgress(100);
    
    return {
      fields: detectedFields,
      totalFields: detectedFields.length,
      hasFormFields: detectedFields.length > 0,
    };
    
  } catch (error) {
    throw new Error(`Form detection failed: ${error.message}`);
  }
};

// Detect form-like structures using OCR (for scanned forms)
export const detectFormStructuresOCR = async (file, onProgress) => {
  onProgress(0);
  
  try {
    // Convert PDF first page to image for OCR analysis
    // This is a simplified approach - in production, you'd use pdf.js or similar
    
    onProgress(20);
    
    // Use Tesseract to detect text and potential form fields
    const worker = await Tesseract.createWorker('eng', 1, {
      logger: m => {
        if (m.status === 'recognizing text') {
          const progress = Math.round((m.progress * 100));
          onProgress(20 + (progress * 0.6));
        }
      }
    });
    
    // Note: This requires converting PDF to image first
    // For now, we'll return a structure indicating OCR-based detection
    const detectedStructures = {
      textBlocks: [],
      potentialFields: [],
      labels: [],
    };
    
    // This would require PDF to image conversion first
    // Placeholder structure for OCR-based form detection
    
    await worker.terminate();
    
    onProgress(100);
    
    return detectedStructures;
    
  } catch (error) {
    throw new Error(`OCR form detection failed: ${error.message}`);
  }
};

// Auto-detect form field types and suggest field names
export const autoDetectFieldTypes = async (formFields) => {
  const suggestions = [];
  
  formFields.forEach((field) => {
    const fieldName = field.name.toLowerCase();
    const fieldValue = (field.value || '').toLowerCase();
    
    let suggestedType = 'text';
    let suggestedLabel = field.name;
    
    // Email detection
    if (fieldName.includes('email') || fieldValue.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      suggestedType = 'email';
    }
    // Phone detection
    else if (fieldName.includes('phone') || fieldName.includes('tel') || 
             fieldValue.match(/[\d\s\-\(\)\+]{10,}/)) {
      suggestedType = 'tel';
    }
    // Date detection
    else if (fieldName.includes('date') || fieldValue.match(/\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/)) {
      suggestedType = 'date';
    }
    // Number detection
    else if (fieldName.includes('amount') || fieldName.includes('price') || 
             fieldName.includes('quantity') || fieldValue.match(/^\d+\.?\d*$/)) {
      suggestedType = 'number';
    }
    // URL detection
    else if (fieldName.includes('url') || fieldName.includes('website') ||
             fieldValue.match(/^https?:\/\//)) {
      suggestedType = 'url';
    }
    // Address detection
    else if (fieldName.includes('address') || fieldName.includes('street') ||
             fieldName.includes('city') || fieldName.includes('zip')) {
      suggestedType = 'text';
      suggestedLabel = fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    
    suggestions.push({
      fieldName: field.name,
      suggestedType: suggestedType,
      suggestedLabel: suggestedLabel,
      confidence: 0.8,
    });
  });
  
  return suggestions;
};

// Extract form data as JSON
export const extractFormDataAsJSON = async (file, onProgress) => {
  onProgress(0);
  
  const formFields = await detectFormFields(file, (progress) => {
    onProgress(progress * 0.7);
  });
  
  onProgress(70);
  
  const jsonData = {};
  formFields.fields.forEach((field) => {
    jsonData[field.name] = {
      type: field.type,
      value: field.value || field.checked || field.selected || null,
      required: field.required,
    };
  });
  
  onProgress(90);
  
  const jsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  
  onProgress(100);
  
  return {
    blob,
    filename: file.name.replace('.pdf', '_form_data.json'),
    data: jsonData,
  };
};

// Validate form fields
export const validateFormFields = async (file, validationRules, onProgress) => {
  onProgress(0);
  
  const formFields = await detectFormFields(file, (progress) => {
    onProgress(progress * 0.5);
  });
  
  onProgress(50);
  
  const validationResults = [];
  
  formFields.fields.forEach((field, index) => {
    const rules = validationRules[field.name] || {};
    const errors = [];
    
    // Required field validation
    if (rules.required && !field.value && !field.checked && !field.selected) {
      errors.push('This field is required');
    }
    
    // Email validation
    if (rules.type === 'email' && field.value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(field.value)) {
        errors.push('Invalid email format');
      }
    }
    
    // Min/Max length validation
    if (rules.minLength && field.value && field.value.length < rules.minLength) {
      errors.push(`Minimum length is ${rules.minLength}`);
    }
    if (rules.maxLength && field.value && field.value.length > rules.maxLength) {
      errors.push(`Maximum length is ${rules.maxLength}`);
    }
    
    // Pattern validation
    if (rules.pattern && field.value) {
      const regex = new RegExp(rules.pattern);
      if (!regex.test(field.value)) {
        errors.push('Value does not match required pattern');
      }
    }
    
    validationResults.push({
      fieldName: field.name,
      isValid: errors.length === 0,
      errors: errors,
    });
    
    onProgress(50 + (index / formFields.fields.length) * 40);
  });
  
  onProgress(100);
  
  return {
    isValid: validationResults.every(r => r.isValid),
    results: validationResults,
  };
};


